import { Box, Button, Grid, useTheme } from '@mui/material';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { ButtonDisplay } from './ButtonDisplay';
import CheckoutInfoView from 'src/components/domain/checkout-info/CheckoutInfoView';
import LoggedInPanel from 'src/components/domain/timeslots-authentication/logged-in-panel/LoggedInPanel';
import LoggedOutPanel from 'src/components/domain/timeslots-authentication/logged-out-panel/LoggedOutPanel';
import { useAtom } from 'ximple';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';
import { cartAtom } from 'src/state/cart/cartAtom';
import { voucherAtom } from 'src/state/voucher/voucherAtom';
import { companyAtom } from 'src/state/company';
import { checkoutInfoAtom } from 'src/state/checkout-info/checkoutInfoAtom';
import { useConfigurations, useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import { useMemberContext } from 'src/widgets/timeslots/timeslots/MemberContext';
import { useCartCheckoutContext } from '../CartCheckoutContext';
import CheckoutSummary, { useSummaryFeatures } from '../../CheckoutSummary';
import useCartItemsForSummary from 'src/hooks/domain/useCartItemsForSummary';
import NativeCheckoutLoginPanel from 'src/widgets/native-membership/NativeCheckoutLoginPanel';

type ContactViewProps = {
    handleGoPayClicked: () => void;
    handleGoPayClickedInvoice?: () => void;
    clickedNext: boolean;
    nextButtonText: string;
};

export function ContactView({
    handleGoPayClicked,
    handleGoPayClickedInvoice,
    clickedNext,
    nextButtonText,
}: ContactViewProps) {
    const theme = useTheme();
    const [{ currentSite }] = useAtom(companyAtom);
    const customizations = useCustomizations();
    const configurations = useConfigurations();
    const [cartItems] = useAtom(cartAtom);
    const [{ appliedPromoCode }] = useAtom(voucherAtom);
    const [checkoutInfo] = useAtom(checkoutInfoAtom);
    const { loggedInUser } = useMemberContext();
    const isNative = useMembershipMode() === 'native';
    const { activeTab, largeScreen, isMakingReservation, showAddressFields } =
        useCartCheckoutContext();

    const cartItemsForSummary = useCartItemsForSummary();

    const showShare = true;
    const enableCartClearing = true;
    const enableItemEditing = true;

    const summaryFeatures = useSummaryFeatures(
        showShare,
        enableCartClearing,
        enableItemEditing,
        activeTab,
        cartItemsForSummary.isWaitingListMode,
    );

    useWidgetEventEffect(() => ({
        eventType: 'checkoutStep',
        checkoutStep: 'Contact Info',
        cartItems: cartItems,
        giftCards: [],
        promoCode: appliedPromoCode,
    }));

    return (
        <Grid
            container
            justifyContent={largeScreen ? 'space-between' : 'center'}
            alignItems={largeScreen ? 'flex-start' : 'center'}
            direction={largeScreen ? 'row' : 'column-reverse'}
        >
            {!largeScreen && (
                <Box
                    maxWidth="440px"
                    width="100%"
                    sx={{
                        [theme.breakpoints.down('md')]: {
                            marginBottom: theme.spacing(3),
                        },
                    }}
                >
                    <Button
                        sx={{ float: 'right', marginTop: theme.spacing(2) }}
                        color="primary"
                        variant={customizations.primaryButtonStyle}
                        disabled={isMakingReservation || cartItems.length === 0}
                        onClick={handleGoPayClicked}
                    >
                        <ButtonDisplay
                            isMakingReservation={isMakingReservation}
                            buttonText={nextButtonText}
                        />
                    </Button>
                </Box>
            )}

            {!isNative && currentSite && largeScreen && (
                <>{loggedInUser ? <LoggedInPanel /> : <LoggedOutPanel checkout="booking" />}</>
            )}
            {isNative && largeScreen && <NativeCheckoutLoginPanel />}

            {checkoutInfo && (
                <CheckoutInfoView showErrors={clickedNext} showAddressFields={showAddressFields} />
            )}

            {!isNative && currentSite && !largeScreen && (
                <>{loggedInUser ? <LoggedInPanel /> : <LoggedOutPanel checkout="booking" />}</>
            )}
            {isNative && !largeScreen && <NativeCheckoutLoginPanel />}

            <CheckoutSummary
                cartItems={cartItemsForSummary.itemsToShow}
                hideGoPay={!largeScreen}
                isMakingReservation={isMakingReservation}
                goPayClicked={handleGoPayClicked}
                goPayClickedInvoice={
                    configurations.enableInvoicePayment && checkoutInfo?.contactType === 'company'
                        ? handleGoPayClickedInvoice
                        : undefined
                }
                summaryFeatures={summaryFeatures}
            />
        </Grid>
    );
}
